import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getRequest } from "../apis/ApiInstence";
import { cachedAxiosGet } from "../utils/apiCache";

const getVideoId = (url) => {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    if (parsed.pathname.startsWith("/shorts/")) {
      return parsed.pathname.split("/shorts/")[1]?.split("/")[0] || "";
    }
    if (parsed.searchParams.get("v")) return parsed.searchParams.get("v");
    const parts = parsed.pathname.split("/");
    return parts[parts.length - 1] || "";
  } catch {
    if (url.includes("shorts/")) return url.split("shorts/")[1]?.split("?")[0] || "";
    if (url.includes("watch?v=")) return url.split("watch?v=")[1]?.split("&")[0] || "";
    return "";
  }
};

const EMBED_HOST = "https://www.youtube.com";

const Herosection = () => {
  const navigate = useNavigate();
  const heroIframeRef = useRef(null);
  const [banners, setBanners] = useState([]);
  const [heroBanners, setHeroBanners] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slideDirection, setSlideDirection] = useState('right');
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({ experience: 0, brands: 0, customers: 0 });
  const [reels, setReels] = useState([]);
  const [heroPlaying, setHeroPlaying] = useState(false);
  const [testimonialImages, setTestimonialImages] = useState([]);
  const [testimonialCount, setTestimonialCount] = useState(0);
  const embedOrigin = typeof window !== "undefined" ? window.location.origin : "";

  const forceHeroPlay = () => {
    const frame = heroIframeRef.current;
    if (!frame || !frame.contentWindow) return;
    frame.contentWindow.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: [] }), "*");
    frame.contentWindow.postMessage(JSON.stringify({ event: "command", func: "mute", args: [] }), "*");
  };

  const combinedSlides = useMemo(() => {
    const slides = [];
    if (banners.length > 0) slides.push({ type: 'benner' });
    heroBanners.forEach(b => {
      slides.push({ type: 'hero', image: b.image, name: b.name || '' });
    });
    return slides;
  }, [banners, heroBanners]);

  const len = combinedSlides.length;

  const goTo = useCallback((index) => {
    if (isTransitioning || len === 0) return;
    setIsTransitioning(true);
    const diff = index - activeSlide;
    const dir = diff > 0 || (diff < -len + 1) ? 'right' : 'left';
    setSlideDirection(dir);
    setActiveSlide((index + len) % len);
    setTimeout(() => setIsTransitioning(false), 700);
  }, [isTransitioning, len, activeSlide]);

  const next = useCallback(() => goTo(activeSlide + 1), [activeSlide, goTo]);
  const prev = useCallback(() => goTo(activeSlide - 1), [activeSlide, goTo]);

  useEffect(() => {
    if (!isAutoPlaying || len === 0 || window.innerWidth < 640) return;
    const timer = setInterval(next, 4500);
    return () => clearInterval(timer);
  }, [isAutoPlaying, next, len]);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      try {
        setIsLoading(true);
        const assetBase = (process.env.REACT_APP_ASSET_BASE_URL || process.env.REACT_APP_BACKEND || "").replace(/\/$/, "");

        const [bannerRes, testimonialRes, reelRes, heroRes] = await Promise.all([
          cachedAxiosGet("benner:banner", () => getRequest("/banner"), 120000),
          cachedAxiosGet("benner:testimonials", () => getRequest("/testimonials?status=active&limit=6"), 300000),
          cachedAxiosGet("benner:reels", () => getRequest("/live-videos?status=active&limit=12"), 180000),
          cachedAxiosGet("benner:herobanners", () => getRequest("/herobanners"), 120000),
        ]);

        const bData = Array.isArray(bannerRes?.data) ? bannerRes.data : (bannerRes?.data?.data || []);
        const normalizedBanners = bData.map(item => ({
          id: item.id,
          model: item.name || "New Arrival",
          desc: item.description || "",
          image: item.image ? (item.image.startsWith("http") ? item.image : `${assetBase}/banner/${String(item.image).replace(/^\/+/, "")}`) : "",
        })).filter(i => i.model);
        if (!cancelled) setBanners(normalizedBanners);

        const tData = Array.isArray(testimonialRes?.data) ? testimonialRes.data : (testimonialRes?.data?.data || []);
        const tImgs = tData.filter(t => t.image).slice(0, 3).map(t => t.image.startsWith("http") ? t.image : `${assetBase}/uploads/${String(t.image).replace(/^\//, "")}`);
        if (!cancelled) {
          setTestimonialImages(tImgs);
          setTestimonialCount(tData.length);
        }

        const rData = Array.isArray(reelRes?.data) ? reelRes.data : (reelRes?.data?.data || []);
        if (!cancelled) setReels(rData.map(r => ({ id: r.id, url: r.url || r.youtube_url || "", platform: r.platform || "youtube" })).filter(r => r.url));

        const hData = Array.isArray(heroRes?.data) ? heroRes.data : (heroRes?.data?.data || []);
        if (!cancelled) setHeroBanners(hData);

      } catch (err) {
        if (!cancelled) console.error("Error loading data:", err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const targets = { experience: 18, brands: 20, customers: 20000 };
    const timer = setInterval(() => {
      setStats(prev => ({
        experience: Math.min(targets.experience, prev.experience + 1),
        brands: Math.min(targets.brands, prev.brands + 1),
        customers: Math.min(targets.customers, prev.customers + 250),
      }));
    }, 40);
    return () => clearInterval(timer);
  }, []);

  const currentSlide = combinedSlides[activeSlide];
  const assetBase = (process.env.REACT_APP_ASSET_BASE_URL || process.env.REACT_APP_BACKEND || 'http://localhost:8000').replace(/\/$/, '');
  const [activeHeroImg, setActiveHeroImg] = useState('');
  const [exitingHeroImg, setExitingHeroImg] = useState('');
  const prevHeroRef = useRef('');

  useEffect(() => {
    if (currentSlide?.type === 'hero') {
      const newImg = `${assetBase}/uploads/herobanner/${currentSlide.image}`;
      if (prevHeroRef.current && prevHeroRef.current !== newImg) {
        setExitingHeroImg(prevHeroRef.current);
        setTimeout(() => setExitingHeroImg(''), 600);
      }
      setActiveHeroImg(newImg);
      prevHeroRef.current = newImg;
    } else {
      setActiveHeroImg('');
      setExitingHeroImg('');
      prevHeroRef.current = '';
    }
  }, [activeSlide, currentSlide, assetBase]);

  if (isLoading || len === 0) {
    return <section className="min-h-[40vh] flex items-center justify-center"><p className="text-sm text-gray-500">Loading...</p></section>;
  }

  return (
    <section
      className="relative overflow-hidden bg-[#f8faff] group"
      style={{ background: "radial-gradient(900px 520px at 70% 10%, rgba(227,233,255,0.6) 0%, rgba(248,250,255,0.98) 45%, #f8faff 100%)" }}
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      <style>{`
        @keyframes slideInRight {
          0% { opacity: 0; transform: translateX(50px) scale(0.96); }
          100% { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes slideInLeft {
          0% { opacity: 0; transform: translateX(-50px) scale(0.96); }
          100% { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes slideOutRight {
          0% { opacity: 1; transform: translateX(0) scale(1); }
          100% { opacity: 0; transform: translateX(-40px) scale(0.96); }
        }
        @keyframes slideOutLeft {
          0% { opacity: 1; transform: translateX(0) scale(1); }
          100% { opacity: 0; transform: translateX(40px) scale(0.96); }
        }
        @keyframes heroZoomIn {
          0% { opacity: 0; transform: scale(1.08); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes heroZoomOut {
          0% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.94); }
        }
        .slide-enter-right {
          animation: slideInRight 0.55s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .slide-enter-left {
          animation: slideInLeft 0.55s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .hero-enter {
          animation: heroZoomIn 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .hero-exit {
          animation: heroZoomOut 0.5s ease-in forwards;
        }
        @media (hover: hover) {
          .banner-arrow-btn {
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.3s ease, transform 0.3s ease;
          }
          .group:hover .banner-arrow-btn {
            opacity: 1;
            pointer-events: auto;
          }
        }
        @media (hover: none) {
          .banner-arrow-btn {
            opacity: 1;
            pointer-events: auto;
          }
        }
        .banner-dot {
          transition: all 0.3s ease;
        }
        :root {
          --pc-primary: #F36F21;
          --pc-ink: #351F72;
          --pc-blue-1: #e3e9ff;
          --pc-blue-2: #d6e0ff;
          --pc-blue-3: #c6d3ff;
        }
        .hero-fade-in { animation: heroFade 0.8s ease forwards; }
        @keyframes heroFade { 0% { opacity: 0; transform: translateY(14px); } 100% { opacity: 1; transform: translateY(0); } }
        .circle-video { position: relative; width: 100%; height: 100%; overflow: hidden; border-radius: 9999px; background: transparent; cursor: pointer; }
        .circle-video video { transform: translate(-50%, -50%); object-fit: cover; position: absolute; top: 50%; left: 50%; width: 100%; height: 100%; }
        .circle-video iframe { position: absolute; top: 50%; left: 50%; width: 190%; height: 190%; transform: translate(-50%, -50%); transform-origin: center; border: 0; }
        .hero-orbit-track { position: absolute; inset: 0; pointer-events: none; animation: orbitSpin 32s linear infinite; }
        .hero-orbit-node { --angle: 0deg; position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%) rotate(var(--angle)) translateY(calc(-1 * clamp(120px, 26vw, 240px))); }
        .hero-orbit-core { display: flex; height: 40px; width: 40px; align-items: center; justify-content: center; border-radius: 9999px; background: #ffffff; box-shadow: 0 12px 24px rgba(243, 111, 33, 0.24); animation: orbitCounter 32s linear infinite; }
        .hero-orbit-icon { display: flex; height: 30px; width: 30px; align-items: center; justify-content: center; border-radius: 9999px; background: linear-gradient(135deg, #f36f21 0%, #e63946 100%); color: white; }
        .hero-ring-spin { animation: orbitSpin 24s linear infinite; transform-origin: center; }
        .hero-ring-bubble { position: absolute; left: 50%; top: -7px; transform: translateX(-50%); display: flex; height: 14px; width: 14px; align-items: center; justify-content: center; border-radius: 9999px; background: linear-gradient(135deg, #f36f21 0%, #e63946 100%); color: white; box-shadow: 0 8px 16px rgba(243, 111, 33, 0.28); font-size: 8px; }
        .hero-main-shell { position: relative; isolation: isolate; background: radial-gradient(75% 75% at 50% 50%, rgba(255, 255, 255, 0.98) 0%, rgba(243, 247, 255, 0.92) 60%, rgba(228, 236, 255, 0.7) 100%); box-shadow: 0 18px 36px rgba(53, 31, 114, 0.14), 0 10px 22px rgba(243, 111, 33, 0.12); }
        .play-btn-facade { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,0.05); transition: background 0.3s; z-index: 5; }
        .play-btn-facade:hover { background: rgba(0,0,0,0.15); }
        .play-icon { width: 56px; height: 56px; background: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 10px 25px rgba(0,0,0,0.15); transition: transform 0.3s ease; }
        @media (min-width: 640px) { .play-icon { width: 72px; height: 72px; } }
        .play-btn-facade:hover .play-icon { transform: scale(1.1); }
        @keyframes orbitSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes orbitCounter { 0% { transform: rotate(0deg); } 100% { transform: rotate(-360deg); } }
        .why-card { transition: transform 0.3s ease, box-shadow 0.3s ease; border: 1px solid rgba(31, 60, 136, 0.08); background: #ffffff; }
        .why-card:hover { transform: translateY(-6px); box-shadow: 0 22px 36px rgba(31, 60, 136, 0.18); }
      `}</style>

      {currentSlide?.type === 'benner' ? (
        <div className="mx-auto w-full max-w-[1400px] py-6 sm:py-10 px-3 sm:px-4">
          <div className="relative grid items-center gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
            {/* Left Content */}
            <div className="relative text-left z-10">
              <div key={activeSlide} className={`slide-enter-${slideDirection}`}>
                {/* Top Badge */}
                <div className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-gray-100 bg-white px-2.5 sm:px-4 py-1 sm:py-1.5 text-[9px] sm:text-xs font-bold text-slate-700 shadow-sm">
                  <i className="fa-solid fa-shield-halved text-[#F36F21] text-[9px] sm:text-inherit"></i>
                  India's Trusted Since 2008
                </div>

                {/* Main Heading */}
                <div className=" flex items-center">
                  <h2 className="text-[35px]  font-bold uppercase tracking-widest text-[#351F72]">PC CARE COMPUTER</h2>
                  <div className="h-[2px] w-8 sm:w-16 bg-gradient-to-r from-[#F36F21] to-transparent"></div>
                </div>
                
                <h1 className=" text-[28px] sm:text-[48px] lg:text-[52px] font-black leading-[1.1] sm:leading-[1.05] tracking-tight text-[#1e293b]">
                  <span className="block text-[#351F72]">Certified</span>
                  <span className="block text-[#F36F21]">Refurbished Laptops</span>
                  <span className="block text-[#351F72]">You Can Trust</span>
                </h1>

                {/* Features Badges */}
                {/* <div className="mt-3 sm:mt-4 flex flex-nowrap sm:flex-wrap gap-1.5 sm:gap-3">
                  <div className="inline-flex items-center justify-center gap-1 sm:gap-2 rounded-full border border-gray-100 bg-white px-2 sm:px-4 py-1 sm:py-2 text-[9px] sm:text-xs font-bold text-slate-700 shadow-sm flex-1 sm:flex-none">
                    <i className="fa-solid fa-laptop text-[#F36F21]/80 text-[8px] sm:text-xs"></i> Any Brand
                  </div>
                  <div className="inline-flex items-center justify-center gap-1 sm:gap-2 rounded-full border border-gray-100 bg-white px-2 sm:px-4 py-1 sm:py-2 text-[9px] sm:text-xs font-bold text-slate-700 shadow-sm flex-1 sm:flex-none">
                    <i className="fa-solid fa-screwdriver-wrench text-slate-400 text-[8px] sm:text-xs"></i> Any Problem
                  </div>
                  <div className="inline-flex items-center justify-center gap-1 sm:gap-2 rounded-full border border-gray-100 bg-white px-2 sm:px-4 py-1 sm:py-2 text-[9px] sm:text-xs font-bold text-slate-700 shadow-sm flex-1 sm:flex-none">
                    <i className="fa-solid fa-shield text-[#F36F21]/80 text-[8px] sm:text-xs"></i> Trusted Support
                  </div>
                </div> */}

                {/* Mobile Circle Section (hidden on lg+) */}
                {(() => {
                  const youtubeReels = reels.filter(r => {
                    const p = r.platform || "";
                    return p === "youtube" || !p;
                  });
                  const heroReel = youtubeReels.length > 0 ? youtubeReels[0] : null;
                  const heroVideoId = heroReel ? getVideoId(heroReel.url) : "";
                  return heroVideoId ? (
                  <div className="lg:hidden my-5 flex justify-center">
                    <div className="hero-main-shell relative z-10 h-[240px] w-[240px] rounded-full sm:h-[410px] sm:w-[410px]">
                      <div className="absolute inset-0 rounded-full bg-[var(--pc-blue-1)]"></div>
                      <div className="absolute inset-4 rounded-full bg-[var(--pc-blue-2)] sm:inset-6"></div>
                      <div className="absolute inset-8 rounded-full bg-[var(--pc-blue-3)] sm:inset-12"></div>
                      
                      <div className="absolute inset-[38px] flex items-center justify-center rounded-full bg-white/80 p-[5px] shadow-xl sm:inset-[78px]">
                        <div className="circle-video" onClick={() => setHeroPlaying(true)}>
                          {heroPlaying ? (
                            <iframe
                              ref={heroIframeRef}
                              onLoad={() => { forceHeroPlay(); setTimeout(forceHeroPlay, 400); }}
                              src={`${EMBED_HOST}/embed/${heroVideoId}?autoplay=1&mute=1&loop=1&playlist=${heroVideoId}&controls=0&modestbranding=1&rel=0&playsinline=1&enablejsapi=1&origin=${encodeURIComponent(embedOrigin)}`}
                              title="Hero Video" allow="autoplay; encrypted-media; fullscreen" frameBorder="0"
                            />
                          ) : (
                            <div className="play-btn-facade">
                              <img 
                                src={`https://i.ytimg.com/vi/${heroVideoId}/hqdefault.jpg`} 
                                alt="Video Thumbnail" 
                                className="absolute inset-0 h-full w-full object-cover"
                              />
                              <div className="play-icon relative z-10">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M8 5V19L19 12L8 5Z" fill="#F36F21" stroke="#F36F21" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="hero-orbit-track">
                        {[
                          { img: "https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg", angle: "0deg", rating: "6.1k+", type: "stars", label: "Reviews" },
                          { img: "https://upload.wikimedia.org/wikipedia/commons/e/e7/Instagram_logo_2016.svg", angle: "90deg", rating: "178k+", type: "count", label: "Followers" },
                          { img: "https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg", angle: "180deg", rating: "4.1k+", type: "count", label: "Followers" },
                          { img: "https://upload.wikimedia.org/wikipedia/commons/0/09/YouTube_full-color_icon_%282017%29.svg", angle: "270deg", rating: "2.71k+", type: "count", label: "Subscribers" }
                        ].map((item, i) => (
                          <div key={i} className="hero-orbit-node" style={{ "--angle": item.angle }}>
                            <div style={{ animation: "orbitCounter 32s linear infinite" }} className="flex flex-col items-center justify-center">
                              <div style={{ transform: `rotate(-${item.angle})` }} className="flex flex-col items-center">
                                <div className="flex h-10 w-10 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-white shadow-[0_8px_24px_rgba(0,0,0,0.12)] border border-gray-100">
                                  <img src={item.img} alt="Social" className="h-5 w-5 sm:h-7 sm:w-7 object-contain" />
                                </div>
                                <div className="mt-1 flex sm:mt-1.5 flex-col items-center justify-center rounded-lg bg-white px-2 sm:px-2.5 py-0.5 sm:py-1 shadow-md border border-gray-100 whitespace-nowrap">
                                  <span className="flex items-center gap-1 text-[10px] sm:text-xs font-black text-slate-800">
                                    {item.type === "stars" && <i className="fa-solid fa-star text-amber-400 text-[9px] sm:text-[10px]"></i>}
                                    {item.rating}
                                  </span>
                                  <span className="text-[7px] sm:text-[9px] font-bold text-slate-500 uppercase tracking-wide">{item.label}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  ) : null;
                })()}

                {/* Description */}
                <p className="mt-3 max-w-[540px] text-[13px] sm:text-base font-medium leading-relaxed text-slate-600">
               Trusted since 2008 for quality refurbished laptops.
4.7K+ Google Reviews earned through customer trust.
                </p>

                {/* Buttons */}
                <div className="mt-3 flex flex-row items-center gap-2 sm:gap-3">
                  <button onClick={() => navigate("/products")} aria-label="View our laptop collection" className="group relative inline-flex items-center justify-center gap-1.5 sm:gap-2 rounded-full bg-gradient-to-r from-[#F36F21] to-[#E63946] p-3 sm:px-4 sm:py-2 text-[11px] sm:text-sm font-bold text-white shadow-lg shadow-orange-500/30 transition hover:-translate-y-0.5 hover:shadow-orange-500/40 flex-1 sm:flex-none">
                    Our Product
                    <span className="flex h-4 w-4 sm:h-5 sm:w-5 items-center justify-center rounded-full bg-white text-[#E63946] transition group-hover:translate-x-1">
                      <i className="fa-solid fa-arrow-right text-[8px] sm:text-[9px]"></i>
                    </span>
                  </button>
                  {/* <button onClick={() => navigate("/services")} aria-label="Get technical service support" className="inline-flex items-center justify-center gap-1.5 sm:gap-2 rounded-full border-2 border-[#e1e7fa] bg-white p-3 sm:px-4 sm:py-2 text-[11px] sm:text-sm font-bold text-[#351F72] transition hover:border-[#351F72] hover:text-[#351F72] flex-1 sm:flex-none">
                    <i className="fa-solid fa-headset text-[13px] sm:text-base"></i>
                    Get Support
                  </button> */}
                </div>

                {/* Stats Cards */}
                <div className="mt-6 sm:mt-8 grid grid-cols-3 gap-1.5 sm:grid-cols-3 sm:gap-4 w-full sm:w-[120%] max-w-[650px] relative z-20">
                  {/* Card 1 */}
                  <div className="flex flex-col items-center gap-1 sm:flex-row sm:items-center sm:gap-3 rounded-2xl bg-white p-1.5 sm:p-4 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-gray-50 transition hover:-translate-y-1 hover:shadow-md">
                    <div className="flex h-7 w-7 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-full bg-orange-50 text-orange-500">
                      <i className="fa-solid fa-star text-[10px] sm:text-xl"></i>
                    </div>
                    <div className="text-center sm:text-left">
                      <div className="text-[11px] sm:text-2xl font-black text-slate-800">{stats.experience}+</div>
                      <div className="text-[7px] sm:text-xs font-semibold text-slate-500">Years</div>
                    </div>
                  </div>
                  {/* Card 2 */}
                  <div className="flex flex-col items-center gap-1 sm:flex-row sm:items-center sm:gap-3 rounded-2xl bg-white p-1.5 sm:p-4 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-gray-50 transition hover:-translate-y-1 hover:shadow-md">
                    <div className="flex h-7 w-7 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-500">
                      <i className="fa-solid fa-laptop text-[10px] sm:text-xl"></i>
                    </div>
                    <div className="text-center sm:text-left">
                      <div className="text-[11px] sm:text-2xl font-black text-slate-800">{stats.brands}+</div>
                      <div className="text-[7px] sm:text-xs font-semibold text-slate-500">Brands</div>
                    </div>
                  </div>
                  {/* Card 3 */}
                  <div className="flex flex-col items-center gap-1 sm:flex-row sm:items-center sm:gap-3 rounded-2xl bg-white p-1.5 sm:p-4 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-gray-50 transition hover:-translate-y-1 hover:shadow-md">
                    <div className="flex h-7 w-7 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-indigo-500">
                      <i className="fa-solid fa-users text-[10px] sm:text-xl"></i>
                    </div>
                    <div className="text-center sm:text-left">
                      <div className="text-[11px] sm:text-2xl font-black text-slate-800">{stats.customers}+</div>
                      <div className="text-[7px] sm:text-xs font-semibold text-slate-500">Happy</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Video / Graphic (desktop only) */}
            {(() => {
              const youtubeReels = reels.filter(r => {
                const p = r.platform || "";
                return p === "youtube" || !p;
              });
              const heroReel = youtubeReels.length > 0 ? youtubeReels[0] : null;
              const heroVideoId = heroReel ? getVideoId(heroReel.url) : "";
              return heroVideoId ? (
              <div className="relative mt-2 sm:mt-4 flex justify-center lg:justify-end hidden lg:block">
                <div className="hero-main-shell relative z-10 h-[240px] w-[240px] rounded-full sm:h-[410px] sm:w-[410px] lg:h-[550px] lg:w-[550px]">
                  <div className="absolute inset-0 rounded-full bg-[var(--pc-blue-1)]"></div>
                  <div className="absolute inset-4 rounded-full bg-[var(--pc-blue-2)] sm:inset-6"></div>
                  <div className="absolute inset-8 rounded-full bg-[var(--pc-blue-3)] sm:inset-12"></div>
                  
                  <div className="absolute inset-[38px] flex items-center justify-center rounded-full bg-white/80 p-[5px] shadow-xl sm:inset-[78px] lg:inset-[96px]">
                    <div className="circle-video" onClick={() => setHeroPlaying(true)}>
                      {heroPlaying ? (
                        <iframe
                          ref={heroIframeRef}
                          onLoad={() => { forceHeroPlay(); setTimeout(forceHeroPlay, 400); }}
                          src={`${EMBED_HOST}/embed/${heroVideoId}?autoplay=1&mute=1&loop=1&playlist=${heroVideoId}&controls=0&modestbranding=1&rel=0&playsinline=1&enablejsapi=1&origin=${encodeURIComponent(embedOrigin)}`}
                          title="Hero Video" allow="autoplay; encrypted-media; fullscreen" frameBorder="0"
                        />
                      ) : (
                        <div className="play-btn-facade">
                          <img 
                            src={`https://i.ytimg.com/vi/${heroVideoId}/hqdefault.jpg`} 
                            alt="Video Thumbnail" 
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                          <div className="play-icon relative z-10">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8 5V19L19 12L8 5Z" fill="#F36F21" stroke="#F36F21" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="hero-orbit-track">
                    {[
                      { img: "https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg", angle: "0deg", rating: "6.1k+", type: "stars", label: "Reviews" },
                      { img: "https://upload.wikimedia.org/wikipedia/commons/e/e7/Instagram_logo_2016.svg", angle: "90deg", rating: "178k+", type: "count", label: "Followers" },
                      { img: "https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg", angle: "180deg", rating: "4.1k+", type: "count", label: "Followers" },
                      { img: "https://upload.wikimedia.org/wikipedia/commons/0/09/YouTube_full-color_icon_%282017%29.svg", angle: "270deg", rating: "2.71k+", type: "count", label: "Subscribers" }
                    ].map((item, i) => (
                      <div key={i} className="hero-orbit-node" style={{ "--angle": item.angle }}>
                        <div style={{ animation: "orbitCounter 32s linear infinite" }} className="flex flex-col items-center justify-center">
                          <div style={{ transform: `rotate(-${item.angle})` }} className="flex flex-col items-center">
                            <div className="flex h-10 w-10 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-white shadow-[0_8px_24px_rgba(0,0,0,0.12)] border border-gray-100">
                              <img src={item.img} alt="Social" className="h-5 w-5 sm:h-7 sm:w-7 object-contain" />
                            </div>
                            <div className="mt-1 flex sm:mt-1.5 flex-col items-center justify-center rounded-lg bg-white px-2 sm:px-2.5 py-0.5 sm:py-1 shadow-md border border-gray-100 whitespace-nowrap">
                              <span className="flex items-center gap-1 text-[10px] sm:text-xs font-black text-slate-800">
                                {item.type === "stars" && <i className="fa-solid fa-star text-amber-400 text-[9px] sm:text-[10px]"></i>}
                                {item.rating}
                              </span>
                              <span className="text-[7px] sm:text-[9px] font-bold text-slate-500 uppercase tracking-wide">{item.label}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                  <div className="absolute left-[-6%] top-[36%] z-30 hidden w-[158px] rounded-2xl bg-white/90 p-2.5 shadow-xl sm:left-[-10%] sm:block">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#90b6ff] text-white">
                         <i className="fas fa-trophy"></i>
                      </div>
                      <div>
                        <p className="text-[13px] font-bold text-slate-800">20k+</p>
                        <p className="text-[10px] font-semibold text-slate-600">Happy Customers</p>
                      </div>
                    </div>
                  </div>

                  <div className="absolute -bottom-[6%] right-[-2%] z-30 hidden w-[170px] rounded-2xl bg-white/90 p-2.5 shadow-xl sm:right-[-4%] sm:block">
                    <div className="absolute -top-3 left-4 flex h-7 w-7 items-center justify-center rounded-full bg-[var(--pc-primary)] text-white shadow-lg">
                      <i className="fa-brands fa-google text-[12px]"></i>
                    </div>
                    <div className="pt-1"><p className="text-[12px] font-semibold text-slate-800">Google Reviews</p></div>
                    <div className="mt-1 flex items-center gap-1 text-[11px] text-amber-500">
                      {[1,2,3,4,5].map(s => <i key={s} className="fa-solid fa-star"></i>)}
                      <span className="ml-1 text-[11px] font-bold text-slate-700">4.8</span>
                    </div>
                    <div className="mt-1.5 flex items-center justify-between">
                      <div className="flex -space-x-2.5">
                        {testimonialImages.map((src, i) => (
                          <img key={i} src={src} alt="Client" className="h-7 w-7 rounded-full border-2 border-white object-cover" />
                        ))}
                      </div>
                      <div className="text-[11px] font-semibold text-slate-500">{testimonialCount}+</div>
                    </div>
                  </div>
                </div>
              ) : null;
            })()}
          </div>
        </div>
      ) : (
        <div className="relative w-full overflow-hidden hidden sm:block">
          {exitingHeroImg && (
            <img
              src={exitingHeroImg}
              alt=""
              className="absolute inset-0 w-full h-full object-cover hero-exit"
              style={{ zIndex: 1 }}
            />
          )}
          <img
            key={activeSlide}
            src={activeHeroImg}
            alt={currentSlide?.name || 'Banner'}
            className="w-full h-auto hero-enter relative"
            style={{ zIndex: 2 }}
          />
        </div>
      )}

      {len > 1 && (
        <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-30 hidden sm:flex items-center gap-2 sm:gap-2.5">
          {combinedSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              className="banner-dot rounded-full"
              style={{
                width: i === activeSlide ? "24px" : "7px",
                height: "7px",
                backgroundColor: i === activeSlide ? "#F36F21" : "rgba(255,255,255,0.5)",
              }}
            />
          ))}
        </div>
      )}

      {len > 1 && (
        <>
          <button
            onClick={prev}
            aria-label="Previous slide"
            className="banner-arrow-btn absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-40 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-black/40 backdrop-blur-sm border border-white/20 hidden sm:flex items-center justify-center text-white/80 hover:bg-white/25 hover:text-white hover:scale-110 transition"
          >
            <i className="fas fa-chevron-left text-xs sm:text-sm"></i>
          </button>
          <button
            onClick={next}
            aria-label="Next slide"
            className="banner-arrow-btn absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-40 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-black/40 backdrop-blur-sm border border-white/20 hidden sm:flex items-center justify-center text-white/80 hover:bg-white/25 hover:text-white hover:scale-110 transition"
          >
            <i className="fas fa-chevron-right text-xs sm:text-sm"></i>
          </button>
        </>
      )}
    </section>
  );
};

export default Herosection;
